import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Factory, Save, KeyRound, Eye, EyeOff, PlugZap, RefreshCcw, CheckCircle2, AlertCircle } from "lucide-react";

const FORMAT_LABELS = {
  none: "None (disabled)",
  command_alkon: "Command Alkon (XML)",
  jonel: "Jonel (JSON)",
};

export default function BatcherIntegrationCard() {
  const [cfg, setCfg] = useState({ output_format: "none", api_url: "", api_key_masked: "", has_api_key: false });
  const [format, setFormat] = useState("none");
  const [apiUrl, setApiUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () =>
    Promise.all([
      api.get("/settings/batcher-integration"),
      api.get("/batcher-dispatches?limit=10").catch(() => ({ data: [] })),
    ])
      .then(([s, d]) => {
        setCfg(s.data);
        setFormat(s.data.output_format);
        setApiUrl(s.data.api_url || "");
        setApiKey("");
        setDispatches(d.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { output_format: format, api_url: apiUrl };
      if (apiKey.trim()) payload.api_key = apiKey.trim();
      await api.put("/settings/batcher-integration", payload);
      toast.success("Batcher integration saved");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const downloadDispatch = async (row) => {
    try {
      const r = await api.get(`/batcher-dispatches/${row.id}/download`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: row.mime || "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = row.filename; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const [retrying, setRetrying] = useState(null);
  const retryPush = async (row) => {
    setRetrying(row.id);
    try {
      const r = await api.post(`/batcher-dispatches/${row.id}/push`, {});
      if (r.data?.delivered) toast.success(`Pushed · HTTP ${r.data.delivery_status}`);
      else toast.error(`Push failed · HTTP ${r.data?.delivery_status ?? "?"}: ${(r.data?.delivery_response || "").slice(0, 120)}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Retry failed");
    } finally {
      setRetrying(null);
    }
  };

  const [testing, setTesting] = useState(false);
  const testEndpoint = async () => {
    setTesting(true);
    try {
      const r = await api.post("/settings/batcher-integration/test", {});
      if (r.data?.ok) toast.success(`Plant responded HTTP ${r.data.status}`);
      else toast.error(`Plant did not accept · HTTP ${r.data?.status}: ${(r.data?.response || "").slice(0, 140)}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  if (loading) return null;

  return (
    <div className="card-industrial p-5" data-testid="batcher-integration-card">
      <div className="flex items-center gap-2 mb-3">
        <Factory className="w-4 h-4 text-primary-orange" />
        <div className="label-tag">Batching Plant Integration</div>
      </div>
      <p className="text-xs text-muted-industrial mono mb-4">
        Auto-generate a batch job file whenever a truck is assigned in the War Room, and let
        operators push dockets to the plant computer on demand.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="label-tag">Auto-Batch Output</Label>
          <Select value={format} onValueChange={setFormat}>
            <SelectTrigger data-testid="batcher-format" className="rounded-none bg-transparent border-industrial mono">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none bg-surface border-industrial">
              {Object.entries(FORMAT_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-tag">Plant API URL (optional)</Label>
          <Input
            data-testid="batcher-url"
            value={apiUrl}
            onChange={(e) => setApiUrl(e.target.value)}
            placeholder="https://plant.example.com/batchjobs"
            className="rounded-none bg-transparent border-industrial mono"
          />
        </div>
        <div>
          <Label className="label-tag inline-flex items-center gap-1">
            <KeyRound className="w-3 h-3" /> Batcher API Key
          </Label>
          <div className="relative">
            <Input
              data-testid="batcher-key"
              type={showKey ? "text" : "password"}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={cfg.has_api_key ? `Stored · ${cfg.api_key_masked} (blank = keep)` : "Not set"}
              className="rounded-none bg-transparent border-industrial mono pr-9"
            />
            <button
              type="button"
              onClick={() => setShowKey((s) => !s)}
              className="absolute right-1 top-1/2 -translate-y-1/2 text-muted-industrial hover:text-primary-orange p-1"
              title={showKey ? "Hide" : "Show"}
            >
              {showKey ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <Button
          data-testid="batcher-save"
          onClick={save}
          disabled={saving}
          className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold text-black disabled:opacity-40"
        >
          <Save className="w-3.5 h-3.5 mr-1" /> {saving ? "Saving…" : "Save Integration"}
        </Button>
        <Button
          data-testid="batcher-test"
          variant="outline"
          onClick={testEndpoint}
          disabled={testing || !cfg.api_url}
          title={cfg.api_url ? "Ping the plant API with a hello payload" : "Save an API URL first"}
          className="rounded-none border-industrial uppercase tracking-widest text-xs disabled:opacity-40"
        >
          <PlugZap className="w-3.5 h-3.5 mr-1" /> {testing ? "Pinging…" : "Test Endpoint"}
        </Button>
        <span className="text-[10px] mono uppercase tracking-widest text-muted-industrial">
          Current: <span className="text-primary-orange">{FORMAT_LABELS[cfg.output_format]}</span>
          {cfg.has_api_key && <span className="ml-2">· Key on file</span>}
          {cfg.api_url && <span className="ml-2">· Live push</span>}
        </span>
      </div>

      <div className="mt-5">
        <div className="label-tag mb-2">Recent Dispatches</div>
        {dispatches.length === 0 ? (
          <div className="text-xs mono text-muted-industrial">
            No batch files sent yet. Assign a truck (or hit <span className="text-primary-orange">Send to Batcher</span> on a docket) to see one appear here.
          </div>
        ) : (
          <div className="border border-industrial divide-y divide-industrial">
            {dispatches.map((d) => {
              const pushed = d.delivered === true;
              const failed = d.delivered === false && d.delivery_status !== undefined && d.delivery_status !== null;
              return (
                <div key={d.id} data-testid={`batcher-dispatch-${d.id}`} className="flex items-center gap-3 px-2 py-1.5 text-[11px] mono">
                  <span className="text-muted-industrial">{new Date(d.sent_at).toLocaleString()}</span>
                  <span className="px-1 py-0.5 border border-industrial uppercase tracking-widest text-[9px]">{d.format}</span>
                  <span className="px-1 py-0.5 border border-industrial uppercase tracking-widest text-[9px] text-primary-orange">
                    {d.source === "auto:assign" ? "AUTO" : "MANUAL"}
                  </span>
                  {pushed && (
                    <span
                      title={`Pushed ${d.delivered_at || ""} · HTTP ${d.delivery_status}`}
                      className="inline-flex items-center gap-1 px-1 py-0.5 border border-emerald-500 text-emerald-400 bg-emerald-500/10 uppercase tracking-widest text-[9px] font-bold"
                    >
                      <CheckCircle2 className="w-2.5 h-2.5" /> PUSHED
                    </span>
                  )}
                  {failed && (
                    <span
                      title={d.delivery_response || "Push failed"}
                      className="inline-flex items-center gap-1 px-1 py-0.5 border border-red-500 text-red-400 bg-red-500/10 uppercase tracking-widest text-[9px] font-bold"
                    >
                      <AlertCircle className="w-2.5 h-2.5" /> FAILED · {d.delivery_status || "ERR"}
                    </span>
                  )}
                  <span className="text-ink">{d.filename}</span>
                  <span className="text-muted-industrial">· {d.truck_number || "—"}</span>
                  <span className="ml-auto flex items-center gap-2">
                    <span className="text-muted-industrial">{d.sent_by_name}</span>
                    {cfg.api_url && (
                      <button
                        type="button"
                        data-testid={`batcher-push-${d.id}`}
                        disabled={retrying === d.id}
                        onClick={() => retryPush(d)}
                        title={pushed ? "Push again" : "Retry push to plant"}
                        className={`hover:underline uppercase tracking-widest text-[9px] inline-flex items-center gap-1 disabled:opacity-40 ${pushed ? "text-emerald-400" : "text-primary-orange"}`}
                      >
                        <RefreshCcw className="w-2.5 h-2.5" />
                        {retrying === d.id ? "Pushing…" : (pushed ? "Repush" : "Push")}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => downloadDispatch(d)}
                      className="text-primary-orange hover:underline uppercase tracking-widest text-[9px]"
                    >
                      Download
                    </button>
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
